import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  ROLE_PERMISSION_DESCRIPTIONS,
  ROLE_PERMISSION_KEYS,
  ROLE_PERMISSION_LABELS,
  canManageRolesFromToken,
  type RolePermissionKey,
  type RolePermissions,
} from '../../../utils/rolePermissions';

interface BusinessRole {
  roleId: string;
  roleName: string;
  description: string;
  permissions: RolePermissions;
  isWebAdmin: boolean;
  configurable: boolean;
}

const emptyPermissions = (): RolePermissions =>
  ROLE_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {} as RolePermissions);

const RolesSettings: React.FC = () => {
  const [roles, setRoles] = useState<BusinessRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const canManage = canManageRolesFromToken();

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list: BusinessRole[] = (Array.isArray(response.data) ? response.data : [])
        .filter((role: BusinessRole) => role.configurable)
        .map((role: BusinessRole) => ({
          ...role,
          permissions: {
            ...emptyPermissions(),
            ...(role.permissions || {}),
          },
        }));

      setRoles(list);
    } catch (err: any) {
      console.error('Failed to load roles:', err);
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const updatePermission = (roleId: string, key: RolePermissionKey, value: boolean) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.roleId !== roleId) return role;
        if (role.roleName === 'Admin' && key === 'manage_roles') {
          return role;
        }
        return {
          ...role,
          permissions: {
            ...role.permissions,
            [key]: value,
          },
        };
      })
    );
    setSuccess(null);
  };

  const updateWebAdmin = (roleId: string, value: boolean) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.roleId !== roleId) return role;
        if (role.roleName === 'Admin') return role;
        return { ...role, isWebAdmin: value };
      })
    );
    setSuccess(null);
  };

  const saveRole = async (role: BusinessRole) => {
    try {
      setSavingRoleId(role.roleId);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.patch(
        `${API_BASE_URL}/roles/${role.roleId}`,
        {
          permissions: role.permissions,
          isWebAdmin: role.isWebAdmin,
          description: role.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = response.data?.role as BusinessRole | undefined;
      if (updated) {
        setRoles((prev) =>
          prev.map((r) =>
            r.roleId === role.roleId
              ? {
                  ...r,
                  ...updated,
                  permissions: {
                    ...emptyPermissions(),
                    ...(updated.permissions || {}),
                  },
                }
              : r
          )
        );
      }

      setSuccess(
        `${role.roleName} permissions saved. Users with this role may need to sign in again for all changes to apply.`
      );
    } catch (err: any) {
      console.error('Failed to save role:', err);
      setError(err.response?.data?.message || `Failed to save ${role.roleName}`);
    } finally {
      setSavingRoleId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canManage) {
    return (
      <Alert severity="warning">
        Only Admins, Super Admins, and Multi Business Managers can configure role permissions.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Lato, sans-serif', fontWeight: 600 }}>
        Role permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Configure what Admin and Team Leader (and other business roles) can do for this business.
        Changes are stored per business, so each organisation can use different settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {roles.length === 0 ? (
        <Alert severity="info">No configurable roles found for this business.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {roles.map((role) => (
            <Paper
              key={role.roleId}
              variant="outlined"
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1.5 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontFamily: 'Lato, sans-serif', fontWeight: 600 }}>
                    {role.roleName}
                  </Typography>
                  {role.description && (
                    <Typography variant="body2" color="text.secondary">
                      {role.description}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    savingRoleId === role.roleId ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={() => saveRole(role)}
                  disabled={savingRoleId === role.roleId}
                >
                  Save {role.roleName}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {ROLE_PERMISSION_KEYS.map((key) => {
                  const locked = role.roleName === 'Admin' && key === 'manage_roles';
                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={!!role.permissions[key]}
                          onChange={(e) => updatePermission(role.roleId, key, e.target.checked)}
                          disabled={locked || savingRoleId === role.roleId}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ROLE_PERMISSION_LABELS[key]}
                            {locked ? ' (required)' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ROLE_PERMISSION_DESCRIPTIONS[key]}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
                    />
                  );
                })}
              </Box>

              {role.roleName !== 'Admin' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!role.isWebAdmin}
                        onChange={(e) => updateWebAdmin(role.roleId, e.target.checked)}
                        disabled={savingRoleId === role.roleId}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Web admin access
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow users with this role to sign in to the web admin
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                </>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RolesSettings;
